/**
 * Leads page intelligence overview header (ADR-009).
 */

import React, { useEffect, useState } from "react";
import { Alert, Col, Empty, Row, Skeleton, Space, Tag, Typography } from "antd";
import { Link } from "react-router-dom";
import {
  MetricTile,
  SectionEyebrow,
  SlotCard,
  formatMoneyZar,
  formatPercent,
} from "@/features/dashboard/components/shared/dashboardShellPrimitives.jsx";
import { getLeadIntelligenceDashboard } from "@/features/dashboard/services/dashboardService.js";

const { Text, Title } = Typography;

export default function LeadIntelligenceOverview({ onFilterDrilldown } = {}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [mapMode, setMapMode] = useState("volume"); // volume | pipeline | expected

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await getLeadIntelligenceDashboard({});
      if (cancelled) return;
      if (!res?.success && res?.error) {
        setError(res.error);
      } else {
        setData(res?.data || res);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <SlotCard data-testid="leads-intelligence-loading" style={{ marginBottom: 16 }}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </SlotCard>
    );
  }

  if (error) {
    return (
      <Alert
        style={{ marginBottom: 16 }}
        type="warning"
        showIcon
        message="Lead intelligence overview unavailable"
        description={error}
        data-testid="leads-intelligence-error"
      />
    );
  }

  if (!data?.enabled || data?.widgetsEnabled === false) {
    return (
      <SlotCard style={{ marginBottom: 16 }} data-testid="leads-intelligence-disabled">
        <Text type="secondary">
          Lead Intelligence overview is disabled. Enable{" "}
          <Text code>leads.intelligence.enabled</Text> and{" "}
          <Text code>leads.intelligence.widgets.enabled</Text> under{" "}
          <Link to="/settings?tab=leads">Settings → Leads → Lead Intelligence</Link>
          .
        </Text>
      </SlotCard>
    );
  }

  const metrics = data.metrics || {};
  const coverage = data.coverage || {};
  const funnel = data.funnel || [];
  const queue = data.priorityQueue || [];
  const cells = data.map?.cells || [];

  const mapValue = (cell) => {
    if (mapMode === "pipeline") return cell.pipelineValue;
    if (mapMode === "expected") return cell.expectedContribution;
    return cell.leadCount;
  };

  return (
    <div data-testid="leads-intelligence-overview" style={{ marginBottom: 16 }}>
      <SlotCard
        title={
          <Space wrap>
            <span>Lead Intelligence</span>
            <Tag color="blue">{data.dataConfidence || "Estimated"}</Tag>
            <Tag color="cyan">Coverage {coverage.coveragePct ?? "—"}%</Tag>
          </Space>
        }
      >
        <SectionEyebrow>Metric strip</SectionEyebrow>
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          {[
            ["New / unworked", metrics.newUnworked?.value, null],
            ["Needs action", metrics.needsAction?.value, null],
            [
              "Pipeline value",
              formatMoneyZar(metrics.activePipelineValue?.amount),
              metrics.activePipelineValue?.dataConfidence,
            ],
            [
              "Weighted pipeline",
              formatMoneyZar(metrics.weightedPipelineValue?.amount),
              metrics.weightedPipelineValue?.dataConfidence,
            ],
            [
              "Lead Value",
              formatMoneyZar(metrics.expectedContribution?.amount),
              metrics.expectedContribution?.dataConfidence,
            ],
          ].map(([label, value, hint]) => (
            <Col xs={24} sm={12} md={8} lg={4} key={label} flex="1 1 18%">
              <MetricTile
                role="button"
                tabIndex={0}
                onClick={() => onFilterDrilldown?.({ label })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onFilterDrilldown?.({ label });
                }}
              >
                <span className="metric-label">{label}</span>
                <span className="metric-value">{value ?? "—"}</span>
                {hint ? <span className="metric-hint">{hint}</span> : null}
              </MetricTile>
            </Col>
          ))}
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Title level={5} style={{ color: "#005249", marginTop: 0 }}>
              Operational funnel
            </Title>
            {funnel.length ? (
              <Space wrap data-testid="leads-intelligence-funnel">
                {funnel.map((stage) => (
                  <Tag key={stage.stage} color="geekblue">
                    {stage.stage}: {stage.count}
                  </Tag>
                ))}
              </Space>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No funnel data" />
            )}
          </Col>
          <Col xs={24} md={12}>
            <Title level={5} style={{ color: "#005249", marginTop: 0 }}>
              Priority queue
            </Title>
            {queue.length ? (
              <Space direction="vertical" style={{ width: "100%" }} data-testid="leads-priority-queue">
                {queue.slice(0, 5).map((item) => (
                  <Link key={item.leadId} to={`/leads/${item.leadId}`}>
                    <Space wrap>
                      <Tag color={item.priority === "P0" ? "red" : "orange"}>
                        {item.priority || "—"}
                      </Tag>
                      <Text>{item.leadId.slice(-6)}</Text>
                      <Text type="secondary">
                        EV {formatMoneyZar(item.expectedContribution)} ·{" "}
                        {formatPercent(item.conversionProbability)}
                      </Text>
                    </Space>
                  </Link>
                ))}
              </Space>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No priority leads" />
            )}
          </Col>
        </Row>

        <div style={{ marginTop: 20 }} data-testid="leads-opportunity-map">
          <Space wrap style={{ marginBottom: 10 }}>
            <Title level={5} style={{ color: "#005249", margin: 0 }}>
              Opportunity map (coarse)
            </Title>
            {[
              ["volume", "Volume"],
              ["pipeline", "Pipeline"],
              ["expected", "Lead Value"],
            ].map(([key, label]) => (
              <Tag
                key={key}
                color={mapMode === key ? "green" : "default"}
                style={{ cursor: "pointer" }}
                onClick={() => setMapMode(key)}
              >
                {label}
              </Tag>
            ))}
          </Space>
          {cells.length ? (
            <Row gutter={[12, 12]}>
              {cells.slice(0, 24).map((cell) => (
                <Col xs={12} sm={8} md={6} key={cell.cellId}>
                  <MetricTile>
                    <span className="metric-label">{cell.cellId}</span>
                    <span className="metric-value">
                      {mapMode === "volume"
                        ? cell.leadCount
                        : formatMoneyZar(mapValue(cell))}
                    </span>
                  </MetricTile>
                </Col>
              ))}
            </Row>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No coarse geo aggregates yet"
            />
          )}
          <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
            Cells are city/region only — no exact customer addresses.
          </Text>
        </div>
      </SlotCard>
    </div>
  );
}
